// externals
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';

import type { ValidationEngine } from '@makemydeal/dr-shared-ui-utils/validationEngine/ValidationEngine';

// types
import type { DraftDealProduct } from '@makemydeal/dr-dash-types';
import type { EditProductInformationProps } from '../../types';

// utils
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';

// styles
import {
    ProductCost,
    ProductName,
    ProductNameContainer,
    IncludedAddOnsContainer,
    AddOnsTitle,
    AddOnsList
} from './EditProductInformation.style';

// constants
import { VppProductType } from '../vehicleProtection/constants';

// components
import { ProviderAddressDetails } from './ProviderAddressDetails';
import ProviderField from './ProviderField';

const EditProductInformation = ({
    productProps,
    onProviderChange,
    onProviderAddressChange,
    validator,
    formErrors,
    onValidationError,
    onOverrideToggle
}: {
    productProps: EditProductInformationProps;
    onProviderChange: (provider: string) => void;
    onProviderAddressChange: (address: DraftDealProduct['productProviderAddress']) => void;
    validator: ValidationEngine;
    formErrors: any;
    onValidationError: (hasError: boolean) => void;
    onOverrideToggle?: (enabled: boolean) => void;
}) => {
    const theme = useInterstateTheme();
    const productCategoryCode = productProps.productCategoryCode?.toUpperCase();

    const includedAddOnsList = productProps.includedAddOnNames.map((item, index) => (
        <li key={index} data-testid={`included-add-on-${index}`}>
            {item}
        </li>
    ));

    const shouldRenderProviderAddress = productCategoryCode === VppProductType.GAP;

    return (
        <div>
            <ProductNameContainer theme={theme}>
                <ProductName data-testid={'product-name'}>{productProps.productName ?? '--'}</ProductName>
                <ProductCost data-testid={'product-initial-cost'}>
                    {formatDollarsAndCents(productProps.productInitialCost)}
                </ProductCost>
            </ProductNameContainer>
            {includedAddOnsList[0] && (
                <IncludedAddOnsContainer data-testid="included-add-ons" theme={theme}>
                    <AddOnsTitle theme={theme}>Included Add-Ons:</AddOnsTitle>
                    <AddOnsList>{includedAddOnsList}</AddOnsList>
                </IncludedAddOnsContainer>
            )}
            <ProviderField
                productCode={productProps.productCode}
                productCategoryCode={productProps.productCategoryCode}
                productProviderName={productProps.productProviderName}
                onProviderChange={onProviderChange}
                onValidationError={onValidationError}
                onOverrideToggle={onOverrideToggle}
            />
            {shouldRenderProviderAddress && (
                <ProviderAddressDetails
                    validator={validator}
                    formErrors={formErrors}
                    providerAddress={productProps.productProviderAddress}
                    onProviderAddressChange={onProviderAddressChange}
                    onValidationError={onValidationError}
                />
            )}
        </div>
    );
};

export default EditProductInformation;
